import { useEffect, useRef, useState } from 'react';
import type { JsonNodeKind } from './jsonTreeNavigation';
import { isExactJsonNumber, type JsonValue } from './jsonTreeEdit';

export type JsonTreeEditableLeafProps = {
  path: string;
  value: unknown;
  kind: JsonNodeKind;
  onCommit: (next: JsonValue) => void;
};

/**
 * Editable rendering of a primitive leaf. Click-to-edit for strings and
 * numbers; an always-visible select for booleans; `null` renders as a
 * non-editable badge — users can remove the entry to drop nulls.
 */
export function JsonTreeEditableLeaf({
  path,
  value,
  kind,
  onCommit,
}: JsonTreeEditableLeafProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(() =>
    kind === 'number' ? String(value) : String(value ?? ''),
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Why the typed number cannot be stored: empty, or beyond what a JavaScript number holds exactly. */
  const [invalid, setInvalid] = useState<'empty' | 'inexact' | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  // Re-sync draft when the underlying value changes from outside (e.g. parent
  // commits a sibling edit and re-renders).
  useEffect(() => {
    if (!editing) {
      setDraft(kind === 'number' ? String(value) : String(value ?? ''));
    }
  }, [value, kind, editing]);

  if (kind === 'boolean') {
    return (
      <select
        data-testid={`json-tree-edit-${path}`}
        value={String(value)}
        className="bg-transparent text-amber-700 dark:text-amber-400 text-xs border border-border rounded px-1"
        onChange={(e) => onCommit(e.target.value === 'true')}
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  if (kind === 'null') {
    return (
      <button
        type="button"
        data-testid={`json-tree-edit-${path}`}
        className="text-muted-foreground italic text-xs border border-dashed border-border rounded px-1 hover:bg-muted"
        onClick={() => onCommit('')}
        title="Replace null with empty string"
      >
        null
      </button>
    );
  }

  const colorClass =
    kind === 'string'
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-sky-600 dark:text-sky-400';

  if (!editing) {
    return (
      <button
        type="button"
        data-testid={`json-tree-edit-${path}`}
        className={`${colorClass} text-left rounded px-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {kind === 'string' ? `"${String(value)}"` : String(value)}
      </button>
    );
  }

  const commit = (fromBlur = false) => {
    setEditing(false);
    if (kind === 'number') {
      const n = Number(draft);
      if (!Number.isFinite(n)) {
        // Reject NaN — fall back to existing value.
        setDraft(String(value));
        return;
      }
      // An emptied field is not 0, and a number beyond double precision would be stored rounded (9007199254740993 →
      // …992): Enter keeps the editor open and says why; leaving the field puts the stored value back.
      const problem = draft.trim() === '' ? 'empty' : isExactJsonNumber(draft) ? null : 'inexact';
      if (problem) {
        if (fromBlur) {
          setDraft(String(value));
          setInvalid(null);
        } else {
          setEditing(true);
          setInvalid(problem);
        }
        return;
      }
      if (!Object.is(n, value)) onCommit(n);
    } else if (draft !== value) {
      onCommit(draft);
    }
  };

  const cancel = () => {
    setDraft(kind === 'number' ? String(value) : String(value ?? ''));
    setEditing(false);
  };

  return (
    <input
      ref={inputRef}
      data-testid={`json-tree-edit-input-${path}`}
      type={kind === 'number' ? 'number' : 'text'}
      value={draft}
      onChange={(e) => { setDraft(e.target.value); setInvalid(null); }}
      onBlur={() => commit(true)}
      aria-invalid={invalid ? true : undefined}
      title={invalid === 'empty' ? 'Enter a number, or press Esc to keep the value.'
        : invalid === 'inexact' ? 'This number is too large or too precise to be stored exactly; enter it in the text view instead.' : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
        // Stop arrow keys from bubbling to the tree-level keydown handler
        // so the user can move the caret inside the input.
        if (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End') {
          e.stopPropagation();
        }
      }}
      className={`${colorClass} bg-transparent border ${invalid ? 'border-destructive' : 'border-border'} rounded px-1 text-xs font-mono w-32`}
    />
  );
}
